import {sendGTMEvent} from '@next/third-parties/google';
import type {AnalyticsEventMap} from './events';

type EventParams = Record<string, unknown>;

const DETAIL_PATH = /^(?:\/[a-z]{2})?\/(projects|phases|unit-types|countries)\/([^/?#]+)/;
const CONTENT_TYPES: Record<string, string> = {projects: 'project', phases: 'phase', 'unit-types': 'unit_type', countries: 'country'};

function compact(params: EventParams): EventParams {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined));
}

export function forwardToGtm(name: string, params: EventParams): void {
  if (typeof window === 'undefined') {
    return;
  }
  sendGTMEvent({event: name, ...params});
}

type MetaCall = ['track' | 'trackCustom', string, EventParams];

// Meta only optimises ads and builds audiences on its standard events, so the
// conversion-shaped events map to those; everything else goes through as a
// custom event under its Firebase name. PageView itself is fired by MetaPixel
// on every route change, so page_view only adds ViewContent on detail pages.
function toMetaCall(name: string, params: EventParams): MetaCall | undefined {
  switch (name) {
    case 'page_view': {
      const match = typeof params.page_path === 'string' ? DETAIL_PATH.exec(params.page_path) : null;
      if (!match) {
        return undefined;
      }
      return ['track', 'ViewContent', {content_type: CONTENT_TYPES[match[1]], content_ids: [match[2]]}];
    }
    case 'submit_register_interest':
      return [
        'track',
        'Lead',
        {
          content_name: 'register_interest',
          content_category: params.item_type,
          content_ids: params.item_id ? [params.item_id] : undefined,
          contact_method: params.contact_method,
        },
      ];
    case 'submit_contact_form':
      return ['track', 'Lead', {content_name: 'contact_form', content_category: params.selected_inquiry_type}];
    case 'contact_method_click':
      return ['track', 'Contact', {content_name: params.item_type}];
    case 'map_click':
      return ['track', 'FindLocation', {content_name: params.item_name, content_ids: params.item_id ? [params.item_id] : undefined}];
    case 'filter_click':
      return ['track', 'Search', {search_string: params.category_name, content_category: params.entry_point}];
    default:
      return ['trackCustom', name, params];
  }
}

const GOOGLE_ADS_LEAD_EVENTS = new Set<keyof AnalyticsEventMap>(['submit_register_interest', 'submit_contact_form']);

export function forwardToGoogleAds(name: keyof AnalyticsEventMap): void {
  if (typeof window === 'undefined' || !GOOGLE_ADS_LEAD_EVENTS.has(name)) {
    return;
  }
  window.gtag_report_conversion?.();
}

export function forwardToMetaPixel(name: string, params: EventParams): void {
  if (typeof window === 'undefined' || !window.fbq) {
    return;
  }
  const call = toMetaCall(name, params);
  if (!call) {
    return;
  }
  const [method, event, eventParams] = call;
  window.fbq(method, event, compact(eventParams));
}
